import classnames from 'classnames';
import {Link} from 'react-router';
import moment from 'moment';
import React, {useMemo, useState} from 'react';

import appStore from 'client/AppStore';
import {useFluxStore} from 'client/framework';
import {Button, Icon, Menu, Modal, NavItem, Text} from '@albert-io/atomic';
import CustomerFeedbackDialogue from 'client/CustomerFeedback/CustomerFeedbackDialogue.react';
import globalNavigationStore from '../GlobalNavigationStore';
import icon from './feedback-icon.svg?raw';
import PointsIndicator from '../PointsIndicator/PointsIndicator';
import sessionStore from 'client/Session/SessionStore';
import {UpgradeButton} from 'generic/UpgradeSubjectButton/UpgradeSubjectButton.react';
import {useTracking} from 'react-tracking';

import {useCurrentUserFeedbackWidgetQuery} from './NavWidgets.queries.generated';

/**
 * Checks the session store and application state to determine whether or not the
 * current session is able to leave feedback.
 */
function canLeaveFeedback() {
  return sessionStore.isTeacher() || sessionStore.isSuper();
}

const NavWidgets = () => {
  const isZoneDennis = useFluxStore(appStore, (s) => s.isZoneDennis());
  const showUpgradeButton = useFluxStore(globalNavigationStore, (s) => s.shouldShowUpgradeButton());
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const hasValidSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const showFeedbackWidget = useFluxStore(sessionStore, () => canLeaveFeedback());

  if (isZoneDennis) {
    return null;
  }

  return (
    <>
      {showUpgradeButton ? (
        <li>
          <NavItem stateless>
            <UpgradeButton />
          </NavItem>
        </li>
      ) : null}
      {isStudent ? <HelpCenterLink /> : <HelpMenu />}
      {hasValidSession && isStudent ? (
        <li>
          <NavItem stateless as='span'>
            <PointsIndicator />
          </NavItem>
        </li>
      ) : null}

      {
        /**
         * We only render the `<FeedbackWidget`> if the session and application state
         * _can_ leave feedback.
         */
        showFeedbackWidget && <FeedbackWidget />
      }
    </>
  );
};

export const HelpMenu = ({isForDrawer = false, wrapperClassName}) => {
  const {trackEvent} = useTracking({
    event: 'click',
    type: 'link',
    feature: null,
    component: 'primary nav',
    content: 'Help Center'
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const showBlogLink = useFluxStore(sessionStore, (s) => s.hasValidSession() && !s.isStudent());

  if (isForDrawer) {
    return (
      <>
        <Button
          className='global-nav-drawer__link global-nav-drawer__assignments-dropdown w-full'
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          Help & Resources
          <Icon icon={dropdownOpen ? 'caret-up' : 'caret-down'} className='ml-x1' />
        </Button>
        {dropdownOpen && (
          <>
            <a
              href='https://help.albert.io'
              className='global-nav-drawer__link global-nav-drawer__assignments-subitem'
              onClick={(e) => {
                e.stopPropagation();
                trackEvent();
              }}
            >
              Help Docs
            </a>
            {showBlogLink && (
              <a
                href='https://www.albert.io/blog'
                className='global-nav-drawer__link global-nav-drawer__assignments-subitem'
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Blog
              </a>
            )}
            <a
              href='https://app.lemcal.com/@albert-adoption-team/teacher-talk'
              className='global-nav-drawer__link global-nav-drawer__assignments-subitem'
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Live Teacher Support Session
            </a>
          </>
        )}
      </>
    );
  }

  return (
    <Menu wrapperClassName={wrapperClassName} closeOnItemClick>
      <Menu.Trigger>
        {(triggerProps) => (
          <NavItem {...triggerProps} as='div'>
            Help & Resources
            <Icon icon='caret-down' className='ml-x1' />
          </NavItem>
        )}
      </Menu.Trigger>
      <Menu.Content className='global-nav__help-menu'>
        <Menu.Item
          leftIconProps={{icon: 'life-ring'}}
          onClick={() => {
            trackEvent();
            window.location.href = 'https://help.albert.io';
          }}
        >
          Help Docs
        </Menu.Item>
        {showBlogLink && (
          <Menu.Item
            leftIconProps={{icon: 'graduation-cap'}}
            onClick={() => (window.location.href = 'https://www.albert.io/blog')}
          >
            Blog
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          className='global-nav__schedule-icon'
          leftIconProps={{icon: 'comments'}}
          onClick={() => {
            window.location.href = 'https://app.lemcal.com/@albert-adoption-team/teacher-talk';
          }}
        >
          <div className='flex flex-1 flex-col'>
            <Text size='m'>Live Teacher Support Session</Text>
            <Text color='tertiary' size='xs'>
              Book your free 15-minute personalized training.
            </Text>
          </div>
        </Menu.Item>
      </Menu.Content>
    </Menu>
  );
};

export const HelpCenterLink = (props) => {
  const {trackEvent} = useTracking({
    event: 'click',
    type: 'link',
    feature: null,
    component: 'primary nav',
    content: 'Help Center'
  });
  return (
    <NavItem
      {...props}
      icon='question-circle'
      href='https://help.albert.io'
      onClick={() => trackEvent()}
    >
      Help
    </NavItem>
  );
};

export const FeedbackWidget = () => {
  const {data, loading} = useCurrentUserFeedbackWidgetQuery();
  const reminderDate = data?.currentUser?.customerFeedbackReminderDate;

  const [shouldIconPulse, setShouldIconPulse] = useState(true);
  const [clearIcon, setClearIcon] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Decided once the query resolves: a moment() comparison in render() only advances with
  // re-renders, which compiled (React Compiler) parents no longer deliver.
  const showRatingIcon = useMemo(
    () => canLeaveFeedback() && reminderDate != null && moment(reminderDate) <= moment(),
    [reminderDate]
  );

  if (loading) {
    return <div />;
  }

  if (!showRatingIcon) {
    return null;
  }

  const handleToggle = () => setShowModal((prevShowModal) => !prevShowModal);

  const handleClick = () => {
    setShouldIconPulse(false);
    handleToggle();
  };

  const ratingIconClassNames = classnames({
    'global-nav__rating-icon': !shouldIconPulse,
    'global-nav__rating-icon-pulsing': shouldIconPulse
  });

  return (
    <li>
      {!clearIcon && (
        <NavItem
          className='global-nav__rating-icon-container'
          role='button'
          aria-label='Leave feedback for Albert'
          tabIndex={0}
          as='div'
          active={showModal}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleClick();
            }
          }}
          onClick={handleClick}
        >
          <div className={ratingIconClassNames} dangerouslySetInnerHTML={{__html: icon}} />
        </NavItem>
      )}
      {showModal && (
        <Modal
          className='customer-feedback-modal'
          ariaLabel='Customer Feedback Modal'
          handleClose={handleToggle}
          longModal
        >
          {() => (
            <CustomerFeedbackDialogue
              name={data?.currentUser?.firstName ?? ''}
              handleClose={handleToggle}
              inModal
              fromNavbar
              clearIcon={() => setClearIcon(true)}
            />
          )}
        </Modal>
      )}
    </li>
  );
};

export default NavWidgets;
